import { client } from '../../axiosClient'
import { badFaithFactor } from '../../utils/helpers/constants'
import { UsersWithLoad } from '../../utils/types/search'
import { Entity, ResponseData } from '../../utils/types/common'
import { Employee } from '../../utils/types/employee'
import { Competence, Skill } from '../../utils/types/skills'
import { getSkillsSum } from '../../utils/functions/common'
import { UserProfile } from '../../utils/types/auth'
import { AdditionalInfo, ContributionData } from '../../utils/types/profile'
import { User } from '../../utils/types/users'
import {searchApproveRequests} from "../../pages/approve/api/requests";
import {EmployeeApproveInfo} from "../../search/context/types";

// we need a network request promise cache because the same data is requested in different parts of the app.
// and it's not a matter of efficiency!
// when one request (R1) ends, it puts some data to Redux AND sets the flag that the data is already downloaded,
// while the second request (R2) is still running (awaiting the same data from the backend).
// but there are OTHER requests which wait for the end of R (relying on the flag in Redux),
// and they fire too early, simultaneously with R2, and a racing condition appears -
// see for instance https://dev.azure.com/quantori/org-skill-matrix-service/_workitems/edit/23949/
// it's too late to fix the logic of the app, but we can avoid the consequences.
// this promise cache, if R2 fires while R1 is still active, just returns THE SAME PROMISE,
// and both R1 and R2 await it and set the same data to Redux and set the same flag at the same moment, no racing conditions.
// and, to boot, it improves efficiency (no doubling in network requests) and the backend load.
class RequestCache<ResultType> {
    cache: Record<string,Promise<ResultType>> = {};
    async get(key: string, query: ()=>Promise<ResultType>):Promise<ResultType> {
        if ( key in this.cache ) {
            //console.log("cache key="+key+" hit");
            return this.cache[key];
        }
        const res = query();
        //console.log("cache key="+key+" add");
        this.cache[key]=res;
        res.then( () => {
            //console.log("cache key="+key+" remove");
            delete this.cache[key];
        } )
        return res;
    }
}

export interface FoundSkill extends Entity {
    category_id: number
    competency_id: number
}

interface Version {
    version: string
}

interface Secure {
    is_secure: boolean
}

const GET_ADDITIONAL_INFO = (gid: string) =>
    `{userByGid (gids: "${gid}") {\n` +
    'certificates\n skills\n position\n availability\n solidLineManager\n isExternalConsultant\n nearestAvailability\n nearestAvailabilityDay\n' +
    '}}'

const GET_LOADS_QUERY = (gids: string) =>
    `{userByGid(gids:${gids}){\n` +
    ' globalId\n' +
    '    totalLoad\n' +
    '    availability\n' +
    '    nearestAvailability\n' +
    '    nearestAvailabilityDay\n' +
    '  }}'

function fetchCompetencies(): Promise<ResponseData<Competence>> {
    return client.get<ResponseData<Competence>>('/api/competencies')
}

function fetchCategories(): Promise<ResponseData<Entity>> {
    return client.get<ResponseData<Entity>>('/api/categories')
}

function fetchAllUsers(): Promise<ResponseData<User>> {
    return client.get<ResponseData<User>>('/api/users')
}

function fetchAllUsersWithCreateCrPermission(): Promise<ResponseData<User>> {
    return client.get<ResponseData<User>>('api/actions/cuacr/users')
}

function getSkillsInCompetencies(competencies: string[]): Promise<ResponseData<Skill>> {
    const search = new URLSearchParams(competencies.map((c) => ['competency_ids', c]))
    const url = `/api/competencies/skills?${search.toString()}`
    return client.get<ResponseData<Skill>>(url)
}

function getSkillsInCategoryInCompetence(
    competenceId: number | undefined,
    categoryId: number | undefined
): Promise<ResponseData<Skill>> {
    return client.get<ResponseData<Skill>>(`/api/skills/competency/${competenceId}/category/${categoryId}`)
}

function sendContributionRequest(data: ContributionData): Promise<any> {
    return client.post(`/api/contribute`, data)
}

function fetchSkills(skillName: string = ''): Promise<ResponseData<FoundSkill>> {
    return client.get<ResponseData<FoundSkill>>(`/api/skills?q=${skillName}`)
}

const fetchUserCache = new RequestCache<UserProfile>();
const approveRequestsCache = new RequestCache<EmployeeApproveInfo[]|undefined>();

async function fetchUser(id: string): Promise<UserProfile> {
    const [userProfile,approveRequests]=await Promise.all([
        fetchUserCache.get(id,() => client.get(`api/users/${id}`)),
        approveRequestsCache.get("",()=>searchApproveRequests(""))
    ]);

    const skills_to_approve=!!approveRequests?.length;
    return { ...userProfile, skills_to_approve };
}

const fetchExtraUserInfoCache = new RequestCache<AdditionalInfo>();

async function fetchExtraUserInfo(id: string): Promise<AdditionalInfo> {
    return fetchExtraUserInfoCache.get(id,()=>
        client.post<AdditionalInfo>('/api/staffing/proxy', { query: GET_ADDITIONAL_INFO(id) })
    );
}

function fetchAppVersion(): Promise<Version> {
    return client.get<Version>('/api/version')
}

function fetchIsSecured(): Promise<Secure> {
    return client.get<Secure>('/api/secured')
}

function getFetchedEmployeesLoadsThroughProxy(gids: string[]): Promise<UsersWithLoad> {
    return client.post('/api/staffing/proxy', {
        query: GET_LOADS_QUERY(JSON.stringify(gids)),
    })
}

function fetchEmployees(): Promise<ResponseData<Employee>> {
    return client.get<ResponseData<Employee>>('/api/users/admin')
}

export {
    fetchUser,
    fetchCompetencies,
    fetchCategories,
    fetchExtraUserInfo,
    getSkillsInCompetencies,
    getSkillsInCategoryInCompetence,
    sendContributionRequest,
    fetchSkills,
    fetchAppVersion,
    fetchEmployees,
    fetchAllUsers,
    fetchIsSecured,
    getFetchedEmployeesLoadsThroughProxy,
    fetchAllUsersWithCreateCrPermission,
}

export async function getBadFaithFactor() {
    const res = await fetchEmployees()
    const sum = getSkillsSum(res.data)
    localStorage.setItem(badFaithFactor, JSON.stringify(sum / res.data.length))
}
